import React, { useEffect, useRef } from "react";
import Blok from "../../../interfaces/Storyblok/Blok.interface";

interface HubspotFormProps extends Blok {
  portal_id: string;
  form_id: string;
  region: string;
  form_height?: number;
  purple_background?: boolean;
}

interface JQuery {
  get(index: number): HTMLElement | undefined;
}

declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (options: Record<string, unknown>) => void;
      };
    };
  }
}

const STYLE_ID = "jag-hs-form-styles";

const HUBSPOT_FORM_CSS = `
.jag-hs-form .hs-form,
.jag-hs-form .hs-form * {
  font-family: 'Montserrat', sans-serif;
  box-sizing: border-box;
}

.jag-hs-form .hs-form fieldset {
  max-width: 100% !important;
  border: none;
  padding: 0;
  margin: 0;
}

.jag-hs-form .hs-form .hs-form-field {
  margin-bottom: 1rem;
}

/* Labels */
.jag-hs-form .hs-form label {
  display: block;
  font-size: 0.875rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.375rem;
  font-family: 'Montserrat', sans-serif;
}

.jag-hs-form .hs-form .hs-form-required {
  color: #db3532;
  margin-left: 2px;
}

/* Text-like inputs */
.jag-hs-form .hs-form input[type="text"],
.jag-hs-form .hs-form input[type="email"],
.jag-hs-form .hs-form input[type="tel"],
.jag-hs-form .hs-form input[type="number"],
.jag-hs-form .hs-form input[type="date"],
.jag-hs-form .hs-form input[type="password"],
.jag-hs-form .hs-form select {
  width: 100%;
  height: 52px;
  padding: 0 1rem;
  border: 1.5px solid #ca91cd;
  border-radius: 9999px;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  color: #4b1375;
  background: #ffffff;
  outline: none;
  box-shadow: none;
  transition: border-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
  -webkit-appearance: none;
  appearance: none;
}

.jag-hs-form .hs-form textarea {
  width: 100%;
  min-height: 140px;
  padding: 0.875rem 1rem;
  border: 1.5px solid #ca91cd;
  border-radius: 1.5rem;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  color: #4b1375;
  background: #ffffff;
  outline: none;
  box-shadow: none;
  transition: border-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
  resize: vertical;
}

.jag-hs-form .hs-form input[type="text"]:focus,
.jag-hs-form .hs-form input[type="email"]:focus,
.jag-hs-form .hs-form input[type="tel"]:focus,
.jag-hs-form .hs-form input[type="number"]:focus,
.jag-hs-form .hs-form input[type="date"]:focus,
.jag-hs-form .hs-form input[type="password"]:focus,
.jag-hs-form .hs-form select:focus,
.jag-hs-form .hs-form textarea:focus {
  border-color: #4b1375;
  box-shadow: 0 0 0 3px rgba(75, 19, 117, 0.12);
}

.jag-hs-form .hs-form input::placeholder,
.jag-hs-form .hs-form textarea::placeholder {
  color: #c9c9c9;
  font-weight: 400;
}

/* Checkboxes & radios — reset pill shape, keep accent colour */
.jag-hs-form .hs-form .inputs-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.jag-hs-form .hs-form .inputs-list li {
  margin-bottom: 0.5rem;
}

.jag-hs-form .hs-form .inputs-list label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 400;
  cursor: pointer;
}

.jag-hs-form .hs-form .inputs-list input[type="checkbox"],
.jag-hs-form .hs-form .inputs-list input[type="radio"] {
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
  border-radius: 0.25rem;
  border: 1.5px solid #ca91cd;
  accent-color: #4b1375;
  cursor: pointer;
}

/* Submit button — matches PRIMARY button */
.jag-hs-form .hs-form .hs-button,
.jag-hs-form .hs-form input[type="submit"] {
  display: inline-block;
  background: #3DCFFF;
  color: #4b1375;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.875rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  padding: 0.75rem 1.25rem;
  line-height: 1;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease-in-out;
  -webkit-appearance: none;
  appearance: none;
}

.jag-hs-form .hs-form .hs-button:hover,
.jag-hs-form .hs-form input[type="submit"]:hover {
  background: #00A9F3;
}

/* Validation errors */
.jag-hs-form .hs-form .hs-error-msgs {
  list-style: none;
  margin: 0.25rem 0 0;
  padding: 0;
}

.jag-hs-form .hs-form .hs-error-msgs label,
.jag-hs-form .hs-form .hs-error-msg {
  color: #db3532;
  font-size: 0.75rem;
  font-weight: 400;
  margin: 0;
}

.jag-hs-form .hs-form .hs-input.error,
.jag-hs-form .hs-form input.error,
.jag-hs-form .hs-form select.error,
.jag-hs-form .hs-form textarea.error {
  border-color: #db3532;
}

/* Success / thank-you message */
.jag-hs-form .submitted-message,
.jag-hs-form .submitted-message * {
  font-family: 'Montserrat', sans-serif;
  color: #4b1375;
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
}

/* Multi-column fieldsets */
.jag-hs-form .hs-form fieldset.form-columns-2 .hs-form-field,
.jag-hs-form .hs-form fieldset.form-columns-3 .hs-form-field {
  padding-right: 0.75rem;
}

.jag-hs-form .hs-form fieldset.form-columns-2 .hs-form-field:last-child,
.jag-hs-form .hs-form fieldset.form-columns-3 .hs-form-field:last-child {
  padding-right: 0;
}

/* GDPR / legal consent */
.jag-hs-form .hs-form .legal-consent-container {
  font-size: 0.75rem;
  color: #818181;
  margin-top: 0.5rem;
}

.jag-hs-form .hs-form .legal-consent-container a {
  color: #4b1375;
  text-decoration: underline;
}
`;

const HubspotForm: React.FC<HubspotFormProps> = ({ portal_id, form_id, region, form_height, purple_background }) => {
  const containerId = `hs-form-${portal_id}-${form_id}`;
  const containerRef = useRef<HTMLDivElement>(null);

  // Inject shared stylesheet once per page
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = HUBSPOT_FORM_CSS;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    if (!portal_id || !form_id || !region) return;

    const createForm = () => {
      if (!window.hbspt) return;
      window.hbspt.forms.create({
        region,
        portalId: portal_id,
        formId: form_id,
        target: `#${containerId}`,
        css: "",
        cssRequired: "",
        onFormReady: ($form: JQuery) => {
          // Advance to step 2 so the progress bar starts there
          const el = $form && typeof $form.get === "function" ? $form.get(0) : ($form as unknown as HTMLElement);
          const nextBtn = el?.querySelector<HTMLElement>(
            ".hs-button.next-button, input.hs-button[value='Next'], .hs-button[data-reactid*='next']",
          );
          if (nextBtn) {
            setTimeout(() => nextBtn.click(), 0);
          }
        },
      });
    };

    if (window.hbspt) {
      createForm();
      return;
    }

    const existing = document.querySelector(`script[src*="hsforms.net/forms/v2"]`);
    if (existing) {
      existing.addEventListener("load", createForm);
      return () => existing.removeEventListener("load", createForm);
    }

    const script = document.createElement("script");
    script.src = `https://js-${region}.hsforms.net/forms/v2.js`;
    script.defer = true;
    script.onload = createForm;
    document.body.appendChild(script);

    return () => {
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [portal_id, form_id, region, containerId]);

  if (!portal_id || !form_id || !region) return null;

  const swirl = "https://a.storyblok.com/f/166376/x/5408ad9e66/comms_swirl.svg";

  return (
    <div
      style={{
        backgroundColor: purple_background ? "#7e62bd" : "transparent",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Left swirl */}
      {purple_background && (
        <img
          src={swirl}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0,
            bottom: 0,
            width: "220px",
            maxWidth: "25%",
            pointerEvents: "none",
            objectFit: "contain",
            objectPosition: "center bottom",
          }}
        />
      )}
      {/* Right swirl (mirrored) */}
      {purple_background && (
        <img
          src={swirl}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            width: "220px",
            maxWidth: "25%",
            pointerEvents: "none",
            objectFit: "contain",
            objectPosition: "center top",
            transform: "scaleX(-1) scaleY(-1)",
          }}
        />
      )}
      <div
        style={{
          minHeight: form_height ? `${form_height}px` : undefined,
          maxWidth: "1536px",
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div id={containerId} ref={containerRef} className="jag-hs-form" />
      </div>
    </div>
  );
};

export default HubspotForm;
